import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Brain, TrendingUp, Lightbulb, MessageSquare, 
  ArrowRight, Mic, ArrowLeft, ShieldCheck, 
  FileSpreadsheet, Zap
} from 'lucide-react';
import Footer from '@/components/Footer';

const AIFinancieraInfo = () => {
  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-75 transition-opacity duration-150 flex-shrink-0">
              <img 
                src="/icon-192x192.png" 
                alt="PGastos" 
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold text-stone-900 tracking-tight">PGastos</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/">
                <Button variant="outline" size="sm" className="border-stone-300 hover:bg-stone-100 text-stone-700 text-xs sm:text-sm active:scale-95 transition-all duration-150 ease-spring cursor-pointer">
                  <ArrowLeft className="w-4 h-4 mr-1.5" />
                  Volver
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="sm" className="bg-stone-900 hover:bg-stone-800 text-white font-semibold text-xs sm:text-sm px-3.5 sm:px-4 py-1.5 sm:py-2 rounded-xl active:scale-95 transition-all duration-150 ease-spring cursor-pointer shadow-sm">
                  Ver Planes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-stone-200/70 border border-stone-300 text-stone-800 px-3.5 py-1.5 rounded-full mb-6 text-xs sm:text-sm font-semibold shadow-xs">
              <Brain className="w-4 h-4 text-indigo-600" />
              <span>Inteligencia Financiera</span>
            </div>
            
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-stone-900 mb-6 leading-tight tracking-tight">
              Tu asesor financiero personal{' '}
              <span className="text-indigo-600">con contexto real de tus cuentas</span>
            </h1>

            <p className="text-base sm:text-lg text-stone-600 mb-8 leading-relaxed max-w-2xl mx-auto font-medium">
              No más gráficas difíciles de entender. Conversa directamente con tu asistente en lenguaje natural, descubre fugas de dinero y recibe estrategias concretas para acelerar tu ahorro.
            </p>

            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link to="/?register=true">
                <Button size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm sm:text-base px-8 py-6 rounded-2xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Comenzar con IA Gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border border-stone-300 bg-white hover:bg-stone-100 text-stone-800 font-bold text-sm sm:text-base px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Ver Planes y Precios
                </Button>
              </Link>
            </div>
          </div>

          {/* Demonstration Preview */}
          <div className="max-w-4xl mx-auto mb-20">
            <Card className="p-6 sm:p-10 bg-white border border-stone-200 shadow-xl rounded-3xl">
              <div className="text-center mb-8">
                <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full">
                  Chat IA con Contexto Real
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-stone-900 mt-3 tracking-tight">
                  Respuestas precisas basadas en tus finanzas
                </h2>
              </div>

              <div className="space-y-4 max-w-2xl mx-auto">
                {/* Pregunta Usuario */}
                <div className="flex items-start justify-end gap-3">
                  <div className="bg-stone-900 text-white p-4 rounded-2xl rounded-tr-none text-xs sm:text-sm shadow-sm max-w-[85%] font-medium">
                    <p>“¿En qué categoría gasté más este mes y cómo puedo reducir $1,500 para mi meta de ahorro?”</p>
                  </div>
                </div>

                {/* Respuesta IA */}
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                    <Brain className="w-4 h-4" />
                  </div>
                  <div className="bg-stone-50 border border-stone-200 text-stone-800 p-4 rounded-2xl rounded-tl-none text-xs sm:text-sm shadow-xs space-y-2 max-w-[85%]">
                    <p className="font-bold text-stone-900">Análisis de tus movimientos:</p>
                    <p className="text-stone-600">
                      Tu mayor categoría fue <strong className="text-stone-900">Restaurantes y Delivery ($4,280 MXN)</strong>, un 22% más alto que el mes anterior.
                    </p>
                    <div className="p-2.5 bg-indigo-50/70 border border-indigo-100 rounded-xl text-indigo-950 font-medium">
                      💡 <strong>Estrategia recomendada:</strong> Si limitas las órdenes de delivery de fin de semana a una por semana, liberarás aproximadamente $1,600 MXN sin tocar tus gastos fijos ni compromisos de tarjetas.
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-20">
            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <MessageSquare className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Chat IA Asesor</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Interactúa con un modelo entrenado para finanzas personales con memoria de tus transacciones, tarjetas y metas activas.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <Mic className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Voz con Desglose</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Dicta frases complejas y la IA extrae los montos, conceptos, cuentas involucradas y clasifica automáticamente.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <TrendingUp className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Pronósticos y Tendencias</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Calcula proyecciones de gasto hacia fin de mes para prevenir números rojos antes de que sucedan.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <Lightbulb className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Detección de Gastos Hormiga</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Identifica microtransacciones recurrentes (suscripciones olvidadas, comisiones, antojos diarios) que drenan tu presupuesto.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <FileSpreadsheet className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Exportaciones Profesionales</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Genera reportes auditables en Excel con fórmulas, PDFs estructurados para contabilidad o CSVs listos para importar.
              </p>
            </Card>

            <Card className="p-6 sm:p-7 bg-white border border-stone-200 hover:border-stone-300 shadow-sm hover:shadow-md transition-all duration-200 ease-spring group rounded-2xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4 group-hover:bg-indigo-600 group-hover:scale-110 transition-all duration-200 ease-spring">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-1.5 group-hover:text-indigo-600 transition-colors">Privacidad Estricta</h3>
              <p className="text-sm text-stone-500 leading-relaxed font-medium">
                Tus datos no se utilizan para entrenar modelos públicos externos. La información financiera viaja encriptada de punto a punto.
              </p>
            </Card>
          </div>

          {/* CTA Section */}
          <Card className="p-8 sm:p-14 bg-stone-900 text-white text-center rounded-3xl border border-stone-800 shadow-xl">
            <h2 className="text-2xl sm:text-4xl font-black mb-3 tracking-tight">
              Toma el control con Inteligencia Artificial
            </h2>
            <p className="text-sm sm:text-base text-stone-400 mb-8 max-w-xl mx-auto leading-relaxed font-medium">
              Descubre qué tan fácil es tener a un asesor financiero en tu bolsillo las 24 horas del día.
            </p>
            <div className="flex flex-col sm:flex-row gap-3.5 justify-center">
              <Link to="/?register=true">
                <Button size="lg" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-500 text-white text-sm sm:text-base font-bold px-8 py-6 rounded-2xl shadow-md active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Crear Cuenta Gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/?pricing=true">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border border-stone-700 bg-stone-800 hover:bg-stone-700 text-white text-sm sm:text-base font-bold px-8 py-6 rounded-2xl active:scale-95 hover:scale-[1.02] transition-all duration-150 ease-spring cursor-pointer">
                  Ver Planes Disponibles
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AIFinancieraInfo;
